import React, { useState, useEffect } from 'react';
import RedactedValue from './shared/RedactedValue';
import CopyButton from './shared/CopyButton';
import ConfirmModal from './shared/ConfirmModal';
import CredentialUsage from './shared/CredentialUsage';
import entityMktSettingsService from '../services/partnerMktSettingsService';
import marketplaceService from '../services/marketplaceService';
import marketplacesettingsService from '../services/marketplacesettingsService';
import hotelService from '../services/hotelService';
import accountService from '../services/accountService';

function CredentialList({ credentials, loading, error, search, onSearchChange, onEdit, onDelete, showActions }) {

  const filtered = credentials.filter(cred => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (cred.marketplace_name || '').toLowerCase().includes(s) ||
      (cred.credential_name || '').toLowerCase().includes(s) ||
      (cred.settings || []).some(setting => (setting.setting_name || '').toLowerCase().includes(s));
  });

  return (
    <>
      <div style={{ marginBottom: '1rem' }}>
        <input
          type="text"
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          placeholder="Search by marketplace name or setting..."
          style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
        />
      </div>
      {error && (
        <div style={{ padding: '10px 14px', background: '#fee2e2', color: '#dc2626', borderRadius: '6px', fontSize: '13px', marginBottom: '1rem' }}>{error}</div>
      )}
      {loading ? (
        <div style={{ color: '#64748b', fontSize: '13px' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: '13px', border: '1px dashed #e2e8f0', borderRadius: '8px' }}>
          {search ? 'No credentials match your search.' : 'No credentials found.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filtered.map(cred => (
            <div key={cred.credential_id} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  {cred.marketplace_image && (
                    <img src={cred.marketplace_image} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} />
                  )}
                  <span style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>{cred.credential_name || cred.marketplace_name}</span>
                  <span style={{ fontSize: '12px', color: '#94a3b8' }}>{cred.marketplace_name}</span>
                  {cred.source_name && (
                    <span style={{ fontSize: '11px', color: '#6b7280', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '4px', padding: '1px 6px' }}>
                      Shared from: {cred.source_name}
                    </span>
                  )}
                  <span style={{ fontSize: '12px', color: '#94a3b8' }}>{(cred.settings || []).length} setting{(cred.settings || []).length !== 1 ? 's' : ''}</span>
                </div>
                {showActions && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button type="button" onClick={() => onEdit(cred)}
                      style={{ padding: '4px 12px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '5px', background: '#fff', cursor: 'pointer' }}>
                      Edit
                    </button>
                    <button type="button" onClick={() => onDelete(cred.marketplace_id, cred.credential_name || cred.marketplace_name, cred.credential_id)}
                      style={{ padding: '4px 12px', fontSize: '12px', border: '1px solid #fca5a5', borderRadius: '5px', background: '#fff', color: '#dc2626', cursor: 'pointer' }}>
                      Delete
                    </button>
                  </div>
                )}
              </div>
              <div style={{ padding: '12px 16px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ color: '#6b7280' }}>
                      <th style={{ textAlign: 'left', padding: '4px 8px 8px 0', fontWeight: 500 }}>Setting</th>
                      <th style={{ textAlign: 'left', padding: '4px 0 8px 0', fontWeight: 500 }}>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(cred.settings || []).map(s => (
                      <tr key={s.setting_name} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '6px 8px 6px 0', fontWeight: 500 }}>{s.setting_name}</td>
                        <td style={{ padding: '6px 0' }}>
                          {s.setting_value
                            ? <RedactedValue value={s.setting_value} />
                            : <span style={{ color: '#d1d5db', fontSize: '12px' }}>not set</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function CredentialsTab({ entityType, entityId }) {
  const [subTab, setSubTab] = useState('mine');
  const [credentials, setCredentials] = useState([]);
  const [sharedCredentials, setSharedCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sharedLoading, setSharedLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sharedError, setSharedError] = useState(null);
  const [search, setSearch] = useState('');
  const [sharedSearch, setSharedSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (entityId) {
      fetchCredentials();
      fetchSharedCredentials();
    }
  }, [entityId]); // eslint-disable-line

  const fetchCredentials = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await entityMktSettingsService.listCredentials(entityType, entityId);
      setCredentials(res.credentials || []);
    } catch {
      setError('Failed to load credentials');
    } finally {
      setLoading(false);
    }
  };

  const fetchSharedCredentials = async () => {
    setSharedLoading(true);
    setSharedError(null);
    try {
      const res = await entityMktSettingsService.getSharedWithEntity(entityType, entityId);
      setSharedCredentials(res.credentials || []);
    } catch {
      setSharedError('Failed to load shared credentials');
    } finally {
      setSharedLoading(false);
    }
  };

  const handleDelete = async (marketplaceId, displayName, credentialId) => {
    if (!window.confirm(`Delete credentials "${displayName}"?`)) return;
    try {
      await entityMktSettingsService.deleteCredentials(marketplaceId, credentialId);
      fetchCredentials();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to delete credentials');
    }
  };

  const subTabStyle = (tab) => ({
    padding: '7px 18px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', border: 'none', background: 'none',
    borderBottom: subTab === tab ? '2px solid #1e40af' : '2px solid transparent',
    color: subTab === tab ? '#1e40af' : '#6b7280',
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>Credentials</h3>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b' }}>Manage and view credentials for this {entityType === 'account' ? 'chain' : entityType}.</p>
        </div>
        {subTab === 'mine' && (
          <button type="button" onClick={() => { setEditing(null); setShowModal(true); }}
            style={{ padding: '7px 16px', background: '#1e40af', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 500 }}>
            + Add Credentials
          </button>
        )}
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: '1.25rem' }}>
        <button type="button" style={subTabStyle('mine')} onClick={() => setSubTab('mine')}>My Credentials</button>
        <button type="button" style={subTabStyle('shared')} onClick={() => setSubTab('shared')}>Shared Credentials</button>
      </div>

      {subTab === 'mine' && (
        <CredentialList
          credentials={credentials}
          loading={loading}
          error={error}
          search={search}
          onSearchChange={setSearch}
          onEdit={(cred) => { setEditing(cred); setShowModal(true); }}
          onDelete={handleDelete}
          showActions
        />
      )}

      {subTab === 'shared' && (
        <>
          <p style={{ margin: '0 0 1rem', fontSize: '0.8125rem', color: '#64748b' }}>
            Credentials shared with this {entityType === 'account' ? 'chain' : entityType} from a parent {entityType === 'account' ? 'partner' : 'chain or partner'}.
          </p>
          <CredentialList
            credentials={sharedCredentials}
            loading={sharedLoading}
            error={sharedError}
            search={sharedSearch}
            onSearchChange={setSharedSearch}
            onEdit={() => {}}
            onDelete={() => {}}
            showActions={false}
          />
        </>
      )}

      {showModal && (
        <CredentialModal
          entityType={entityType}
          entityId={entityId}
          editing={editing}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={() => { setShowModal(false); setEditing(null); fetchCredentials(); }}
        />
      )}
    </div>
  );
}

export function CredentialModal({ entityType, entityId, editing, onClose, onSaved, overrides = null, overridesContext = null }) {
  const searchInputRef = React.useRef(null);
  const [inputRect, setInputRect] = React.useState(null);
  const [marketplaces, setMarketplaces] = useState([]);
  // Universal API credentials are MINTED, not typed: the uAPI gateway only accepts a real Cognito app
  // client, so a hand-entered id/secret authenticates against nothing and fails at the customer.
  const [generatingUapi, setGeneratingUapi] = useState(false);
  const [credentialName, setCredentialName] = useState(editing?.credential_name || '');
  const [selectedMarketplaceId, setSelectedMarketplaceId] = useState(editing?.marketplace_id || '');
  const [marketplaceSearch, setMarketplaceSearch] = useState(editing?.marketplace_name || '');
  const [showMarketplaceList, setShowMarketplaceList] = useState(false);
  const [marketplaceSettings, setMarketplaceSettings] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { success, message }
  const [error, setError] = useState(null);
  const [modalTab, setModalTab] = useState('credentials');
  // The caller's overrides, upper-cased by setting name, blanks dropped.
  const usedOverrides = Object.entries(overrides || {})
    .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
    .map(([k, v]) => [String(k).toUpperCase(), String(v)]);
  const overrideOf = (name) => (usedOverrides.find(([k]) => k === String(name).toUpperCase()) || [])[1] || null;

  // Sharing state
  const [shares, setShares] = useState([]); // [{targetEntityType, targetEntityId}]
  const [sharingEntities, setSharingEntities] = useState([]); // chains (partner) or hotels (account)
  const [expandedChains, setExpandedChains] = useState({}); // chainId -> hotels[]
  const [chainHotelsCache, setChainHotelsCache] = useState({}); // chainId -> hotels[] (always populated)
  const [loadingSharing, setLoadingSharing] = useState(false);
  const [sharingSearch, setSharingSearch] = useState('');

  const canShare = entityType === 'account' || entityType === 'partner';
  const activeMarketplaceId = editing?.marketplace_id || selectedMarketplaceId;
  // Matched on name, not id: the marketplace row is created by a migration with an AUTO_INCREMENT id,
  // so it differs between dev and prd and must never be hardcoded.
  const isUapiMarketplace = (editing?.marketplace_name
    || marketplaces.find(m => String(m.MARKETPLACEID) === String(activeMarketplaceId))?.SYSTEMNAME) === 'HCONNECT Universal API';

  // Mint a Cognito app client and drop its id/secret into the form. Deliberately fills the fields
  // rather than saving: the credential is still created by the normal save, so there is one write path
  // and the name/sharing choices on this form still apply.
  const generateUapiCredentials = async () => {
    setGeneratingUapi(true);
    setError(null);
    try {
      const res = await entityMktSettingsService.generateUapiCredentials({ credentialName });
      const d = res.data || res;
      setValues(prev => ({
        ...prev,
        CLIENTID: d.clientId,
        CLIENTSECRET: d.clientSecret,
        // APPKEY / ENTERPRISEID / EXTERNALSYSTEM identify the caller to the Universal API and are minted
        // with the client (V414 added them to this marketplace). Filled ONLY when empty, unlike the
        // client id/secret: pressing Generate again is how a secret is rotated, and rotating a secret
        // must not silently re-identify an integration that is already issued and calling.
        ...(prev.APPKEY || !d.appKey ? {} : { APPKEY: d.appKey }),
        ...(prev.ENTERPRISEID || !d.enterpriseId ? {} : { ENTERPRISEID: d.enterpriseId }),
        ...(prev.EXTERNALSYSTEM || !d.externalSystem ? {} : { EXTERNALSYSTEM: d.externalSystem }),
        // No GATEWAYURL default any more — V415 removed the field from this marketplace. There is one
        // Universal API per environment at a domain the platform owns, so its address is not something a
        // credential carries a copy of. TOKENURL is likewise left alone: a guessed one fails with
        // "invalid_request" and nothing pointing at the URL as the cause.
      }));
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to generate credentials');
    } finally {
      setGeneratingUapi(false);
    }
  };


  // Hand the whole credential over in one file. These values are ISSUED by us and then have to reach
  // whoever is building against the Universal API — copying five fields one at a time into an email is
  // where a truncated secret or a mismatched pair comes from.
  //
  // Built from marketplaceSettings, not from `values`, so the file lists the credential's fields in the
  // marketplace's own order and includes any that are still blank — a recipient can see a value is
  // missing, whereas an absent row just looks like the field does not exist.
  const uapiCsvFilename = () => {
    const base = String(credentialName || 'uapi-credential')
      .trim().toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
    return `${base || 'uapi-credential'}-credentials.csv`;
  };

  const downloadUapiCsv = () => {
    // RFC 4180 quoting: wrap every field and double any embedded quote. A client secret can contain a
    // comma, and an unquoted one silently splits into two columns.
    const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [
      ['Field', 'Value'],
      ['Credential Name', credentialName || ''],
      ...marketplaceSettings.map(s => [s.SETTINGNAME, values[s.SETTINGNAME] || '']),
    ];
    const csv = rows.map(r => r.map(cell).join(',')).join('\r\n');
    // BOM so Excel reads it as UTF-8 rather than the local codepage.
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = uapiCsvFilename();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoked on the next tick, not immediately: revoking before the browser has started the download
    // cancels it in Safari.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  useEffect(() => {
    loadMarketplaces();
    const handleClick = () => setShowMarketplaceList(false);
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (selectedMarketplaceId) {
      loadMarketplaceSettings(selectedMarketplaceId);
      // Do NOT default the credential name to the marketplace name — it leads to every
      // credential being named after its marketplace. The user must give it a distinct name.
    } else {
      setMarketplaceSettings([]);
      setValues({});
    }
  }, [selectedMarketplaceId]); // eslint-disable-line

  // Load sharing data when modal opens for editing, or when marketplace selected for new
  useEffect(() => {
    if (!canShare || !activeMarketplaceId) return;
    loadSharingData(activeMarketplaceId);
  }, [activeMarketplaceId]); // eslint-disable-line

  const loadMarketplaces = async () => {
    try {
      const res = await marketplaceService.getCatalog();
      const list = res.marketplaces || res.settings || [];
      setMarketplaces(list.filter(m => m.MARKETPLACEID > 0 && m.SYSTEMACTIVE === 'Y'));
    } catch (err) {
      setError('Failed to load marketplaces');
    }
  };

  const loadMarketplaceSettings = async (marketplaceId) => {
    setLoading(true);
    try {
      const res = await marketplacesettingsService.getSchema({ MARKETPLACEID: marketplaceId });
      const settings = res.settings || [];
      const authSettings = settings.filter(s => s.required?.toLowerCase() === 'y');
      setMarketplaceSettings(authSettings);

      const prefill = {};
      if (editing && String(editing.marketplace_id) === String(marketplaceId)) {
        editing.settings.forEach(s => { prefill[s.setting_name] = s.setting_value || ''; });
      }
      authSettings.forEach(s => { if (prefill[s.SETTINGNAME] === undefined) prefill[s.SETTINGNAME] = ''; });
      setValues(prefill);
      setTestResult(null);
    } catch (err) {
      setError('Failed to load marketplace settings');
    } finally {
      setLoading(false);
    }
  };

  const loadSharingData = async (marketplaceId) => {
    setLoadingSharing(true);
    try {
      let entities = [];
      if (entityType === 'account') {
        const res = await hotelService.getAllHotels({ accountid: entityId });
        entities = res.hotels || res || [];
      } else if (entityType === 'partner') {
        const res = await accountService.getAllAccounts({ partnerid: entityId });
        entities = res.accounts || res || [];
      }
      setSharingEntities(entities);

      if (!editing || !marketplaceId) return;

      const res = await entityMktSettingsService.getShares(entityType, entityId, marketplaceId, editing.credential_id);
      const normalised = (res.shares || []).flatMap(row => {
        const result = [];
        if (row.shared_with_account_id) result.push({ target_entity_type: 'account', target_entity_id: row.shared_with_account_id });
        if (row.shared_with_hotel_id)   result.push({ target_entity_type: 'hotel',   target_entity_id: row.shared_with_hotel_id });
        return result;
      });
      setShares(normalised);

      // Pre-load every chain's hotels so counts can be shown on collapsed chains and so
      // chains with hotel-level shares can be auto-expanded with their hotels visible.
      if (entityType === 'partner') {
        const hotelShareIds = new Set(
          normalised.filter(s => s.target_entity_type === 'hotel').map(s => String(s.target_entity_id))
        );
        const allHotels   = {};
        const autoExpand  = {};
        const hotelResults = await Promise.all(
          entities.map(chain => hotelService.getAllHotels({ accountid: chain.id })
            .then(r => ({ chainId: chain.id, hotels: r.hotels || r || [] }))
            .catch(() => ({ chainId: chain.id, hotels: [] })))
        );
        for (const { chainId, hotels } of hotelResults) {
          allHotels[chainId] = hotels;
          if (hotelShareIds.size > 0 && hotels.some(h => hotelShareIds.has(String(h.hotelid)))) {
            autoExpand[chainId] = hotels;
          }
        }
        setChainHotelsCache(allHotels);
        setExpandedChains(autoExpand);
      }
    } catch (err) {
      // Non-fatal — sharing section just won't show entities
    } finally {
      setLoadingSharing(false);
    }
  };

  const loadChainHotels = async (chainId) => {
    // Always re-fetch on expand so newly-created hotels show up without re-opening the modal.
    try {
      const res    = await hotelService.getAllHotels({ accountid: chainId });
      const hotels = res.hotels || res || [];
      setChainHotelsCache(prev => ({ ...prev, [chainId]: hotels }));
      setExpandedChains(prev => ({ ...prev, [chainId]: hotels }));
    } catch {
      setExpandedChains(prev => ({ ...prev, [chainId]: [] }));
    }
  };

  const isShared = (targetEntityType, targetEntityId) =>
    shares.some(s => s.target_entity_type === targetEntityType && String(s.target_entity_id) === String(targetEntityId));

  const toggleShare = (targetEntityType, targetEntityId) => {
    setShares(prev => {
      const exists = prev.some(s => s.target_entity_type === targetEntityType && String(s.target_entity_id) === String(targetEntityId));
      if (exists) {
        return prev.filter(s => !(s.target_entity_type === targetEntityType && String(s.target_entity_id) === String(targetEntityId)));
      }
      return [...prev, { target_entity_type: targetEntityType, target_entity_id: targetEntityId }];
    });
  };

  // For account: toggle "all hotels" = share with the account entity itself (type='account', id=entityId)
  const toggleAllHotels = () => toggleShare('account', entityId);
  const allHotelsSelected = isShared('account', entityId);

  // For partner: toggle "all hotels in chain" = share with type='account', id=chainId
  const toggleAllInChain = (chainId) => toggleShare('account', chainId);

  const handleTest = async () => {
    const mktId = editing?.marketplace_id || selectedMarketplaceId;
    if (!mktId) return;
    const settings = marketplaceSettings.map(s => ({ name: s.SETTINGNAME, value: values[s.SETTINGNAME] || '' }));
    setTesting(true);
    setTestResult(null);
    try {
      const res = await entityMktSettingsService.testCredentials(mktId, settings);
      setTestResult(res.data || res);
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.message || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!selectedMarketplaceId && !editing) { setError('Please select a marketplace'); return; }
    if (!credentialName.trim()) { setError('Name is required'); return; }

    const mktId = editing?.marketplace_id || selectedMarketplaceId;
    const missing = marketplaceSettings.filter(s => s.required === 'Y' && !values[s.SETTINGNAME]);
    if (missing.length > 0) {
      setError(`Required fields missing: ${missing.map(s => s.SETTINGNAME).join(', ')}`);
      return;
    }

    const settings = marketplaceSettings.map(s => ({ name: s.SETTINGNAME, value: values[s.SETTINGNAME] || '' }));

    setSaving(true);
    setError(null);
    try {
      const saveRes = await entityMktSettingsService.saveCredentials(entityType, entityId, mktId, credentialName.trim(), settings, editing?.credential_id);
      const savedCredentialId = editing?.credential_id || saveRes?.credentialId;
      if (canShare && savedCredentialId) {
        await entityMktSettingsService.saveShares(entityType, entityId, mktId, savedCredentialId,
          shares.map(s => ({ targetEntityType: s.target_entity_type, targetEntityId: s.target_entity_id }))
        );
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const renderSharingSection = () => {
    if (!canShare || !activeMarketplaceId) return null;

    return (
      <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
        <h4 style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>Share Credentials</h4>
        <p style={{ margin: '0 0 14px', fontSize: '12px', color: '#6b7280' }}>
          {entityType === 'account'
            ? 'Allow hotels in your chain to use these credentials.'
            : 'Allow hotel chains or specific hotels to use these credentials.'}
        </p>

        <input
          type="text"
          value={sharingSearch}
          onChange={e => setSharingSearch(e.target.value)}
          placeholder={entityType === 'account' ? 'Search hotels...' : 'Search chains or hotels...'}
          style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box', marginBottom: '10px' }}
        />
        {loadingSharing ? (
          <div style={{ fontSize: '12px', color: '#94a3b8' }}>Loading...</div>
        ) : entityType === 'account' ? (
          renderAccountSharing()
        ) : (
          renderPartnerSharing()
        )}
      </div>
    );
  };

  const renderAccountSharing = () => {
    const q = sharingSearch.toLowerCase();
    const hotels = sharingEntities.filter(h => !q || h.hotelname.toLowerCase().includes(q));
    return (
      <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: '#f8fafc', cursor: 'pointer', borderBottom: hotels.length > 0 ? '1px solid #e2e8f0' : 'none' }}>
          <input
            type="checkbox"
            checked={allHotelsSelected}
            onChange={toggleAllHotels}
            style={{ width: '14px', height: '14px', cursor: 'pointer' }}
          />
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>All Hotels in Chain</span>
        </label>
        {!allHotelsSelected && hotels.map(hotel => (
          <label key={hotel.hotelid} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px 8px 24px', cursor: 'pointer', borderTop: '1px solid #f1f5f9' }}>
            <input
              type="checkbox"
              checked={isShared('hotel', hotel.hotelid)}
              onChange={() => toggleShare('hotel', hotel.hotelid)}
              style={{ width: '14px', height: '14px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '13px', color: '#374151' }}>{hotel.hotelname}</span>
          </label>
        ))}
        {hotels.length === 0 && !allHotelsSelected && (
          <div style={{ padding: '8px 12px', fontSize: '12px', color: '#94a3b8' }}>No hotels in this chain.</div>
        )}
      </div>
    );
  };

  const renderPartnerSharing = () => {
    const q = sharingSearch.toLowerCase();
    const chains = sharingEntities.filter(chain => {
      if (!q) return true;
      if (chain.name.toLowerCase().includes(q)) return true;
      const chainHotels = chainHotelsCache[chain.id] || expandedChains[chain.id] || [];
      return chainHotels.some(h => (h.hotelname || '').toLowerCase().includes(q));
    });
    if (chains.length === 0) {
      return <div style={{ fontSize: '12px', color: '#94a3b8' }}>{sharingSearch ? 'No results.' : 'No hotel chains associated with this partner.'}</div>;
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {chains.map(chain => {
          const chainId = chain.id;
          const chainName = chain.name;
          const chainAllSelected = isShared('account', chainId);
          // When the user has typed a search query that matches a hotel in this chain,
          // implicitly expand the chain using the cached hotels so the match is visible.
          const cachedHotels   = chainHotelsCache[chainId];
          const searchExpand   = q && !chainAllSelected && cachedHotels?.some(h => (h.hotelname || '').toLowerCase().includes(q));
          const chainHotels    = expandedChains[chainId] !== undefined ? expandedChains[chainId] : (searchExpand ? cachedHotels : undefined);
          const isExpanded     = chainHotels !== undefined;

          return (
            <div key={chainId} style={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', background: '#f8fafc', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={chainAllSelected}
                    onChange={() => toggleAllInChain(chainId)}
                    style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>{chainName}</span>
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                    {chainAllSelected ? 'All hotels' : (chainHotelsCache[chainId] !== undefined ? `${chainHotelsCache[chainId].length} hotel${chainHotelsCache[chainId].length === 1 ? '' : 's'}` : 'All hotels')}
                  </span>
                </label>
                {!chainAllSelected && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!isExpanded) loadChainHotels(chainId);
                      else setExpandedChains(prev => { const next = { ...prev }; delete next[chainId]; return next; });
                    }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: '#6b7280', padding: '2px 6px', borderRadius: '4px', border: '1px solid #e2e8f0', background: '#fff' }}
                  >
                    {isExpanded ? 'Hide hotels' : 'Select hotels'}
                  </button>
                )}
              </div>
              {isExpanded && !chainAllSelected && (
                <div>
                  {(() => {
                    const q = sharingSearch.toLowerCase();
                    const filteredHotels = q ? chainHotels.filter(h => h.hotelname.toLowerCase().includes(q)) : chainHotels;
                    return filteredHotels.length === 0 ? (
                      <div style={{ padding: '8px 12px', fontSize: '12px', color: '#94a3b8' }}>{q ? 'No hotels match.' : 'No hotels in this chain.'}</div>
                    ) : filteredHotels.map(hotel => (
                    <label key={hotel.hotelid} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px 8px 28px', cursor: 'pointer', borderTop: '1px solid #f1f5f9' }}>
                      <input
                        type="checkbox"
                        checked={isShared('hotel', hotel.hotelid)}
                        onChange={() => toggleShare('hotel', hotel.hotelid)}
                        style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '13px', color: '#374151' }}>{hotel.hotelname}</span>
                    </label>
                  ));
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const tabStyle = (tab) => ({
    padding: '8px 16px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', border: 'none', background: 'none',
    borderBottom: modalTab === tab ? '2px solid #1e40af' : '2px solid transparent',
    color: modalTab === tab ? '#1e40af' : '#6b7280'
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: '10px', width: modalTab === 'usage' ? '880px' : '560px', maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
            {editing ? `Edit Credentials — ${editing.marketplace_name}` : 'Add Marketplace Credentials'}
          </h2>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#94a3b8' }}>×</button>
        </div>

        {/* Sharing for a partner's or chain's credential; Usage for any saved one (a new credential is used by nothing). */}
        {(canShare || editing) && (
          <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', padding: '0 24px', flexShrink: 0 }}>
            <button type="button" style={tabStyle('credentials')} onClick={() => setModalTab('credentials')}>Credentials</button>
            {canShare && <button type="button" style={tabStyle('sharing')} onClick={() => setModalTab('sharing')}>Sharing</button>}
            {editing && <button type="button" style={tabStyle('usage')} onClick={() => setModalTab('usage')}>Usage</button>}
          </div>
        )}

        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
          {error && (
            <div style={{ padding: '10px 14px', background: '#fee2e2', color: '#dc2626', borderRadius: '6px', fontSize: '13px', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          {modalTab === 'credentials' && (
            <>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontWeight: 500, fontSize: '13px', color: '#374151', marginBottom: '6px' }}>
                  Name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={credentialName}
                  onChange={e => setCredentialName(e.target.value)}
                  placeholder="e.g., Main OHIP Credentials"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>

              {!editing && (
                <div style={{ marginBottom: '20px', position: 'relative' }}>
                  <label style={{ display: 'block', fontWeight: 500, fontSize: '13px', color: '#374151', marginBottom: '6px' }}>
                    Marketplace <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={marketplaceSearch}
                    onChange={e => { setMarketplaceSearch(e.target.value); setSelectedMarketplaceId(''); setShowMarketplaceList(true); }}
                    onFocus={() => { setShowMarketplaceList(true); if (searchInputRef.current) setInputRect(searchInputRef.current.getBoundingClientRect()); }}
                    placeholder="Search marketplaces..."
                    autoComplete="off"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                  />
                  {showMarketplaceList && inputRect && (
                    <div onMouseDown={e => e.stopPropagation()} style={{ position: 'fixed', top: inputRect.bottom + 2, left: inputRect.left, width: inputRect.width, background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', maxHeight: '200px', overflowY: 'auto', zIndex: 2000, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                      {marketplaces
                        .filter(m => !marketplaceSearch || m.SYSTEMNAME.toLowerCase().includes(marketplaceSearch.toLowerCase()))
                        .map(m => (
                          <div
                            key={m.MARKETPLACEID}
                            onClick={() => { setSelectedMarketplaceId(m.MARKETPLACEID); setMarketplaceSearch(m.SYSTEMNAME); setShowMarketplaceList(false); }}
                            style={{ padding: '8px 12px', fontSize: '13px', cursor: 'pointer', background: selectedMarketplaceId === m.MARKETPLACEID ? '#eff6ff' : '#fff', color: '#374151' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                            onMouseLeave={e => e.currentTarget.style.background = selectedMarketplaceId === m.MARKETPLACEID ? '#eff6ff' : '#fff'}
                          >
                            {m.SYSTEMNAME} <span style={{ color: '#9ca3af' }}>[{m.MARKETPLACEID}]</span>
                          </div>
                        ))}
                      {marketplaces.filter(m => !marketplaceSearch || m.SYSTEMNAME.toLowerCase().includes(marketplaceSearch.toLowerCase())).length === 0 && (
                        <div style={{ padding: '8px 12px', fontSize: '13px', color: '#9ca3af' }}>No results</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {loading && <div style={{ color: '#64748b', fontSize: '13px' }}>Loading settings...</div>}

              {/* Universal API credentials are MINTED, not typed. The gateway's authorizer only accepts a
                  client we issued, so a hand-entered pair is stored happily and then rejected on every
                  call — a failure that surfaces at the customer. Shown only for this marketplace; every
                  other one is a credential we were GIVEN, not one we mint. */}
              {!loading && isUapiMarketplace && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                              background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '6px',
                              marginBottom: '16px' }}>
                  <button
                    type="button"
                    onClick={generateUapiCredentials}
                    disabled={generatingUapi}
                    style={{ padding: '6px 12px', fontSize: '13px', fontWeight: 600, borderRadius: '6px',
                             border: '1px solid #4f46e5', background: generatingUapi ? '#c7d2fe' : '#4f46e5',
                             color: '#fff', cursor: generatingUapi ? 'default' : 'pointer', flexShrink: 0 }}
                  >
                    {generatingUapi ? 'Generating…' : 'Generate Credentials'}
                  </button>
                  {/* Disabled until something is filled in, so an empty file is never handed on as if it
                      were the credential. */}
                  <button
                    type="button"
                    onClick={downloadUapiCsv}
                    disabled={!marketplaceSettings.some(s => values[s.SETTINGNAME])}
                    style={{ padding: '6px 12px', fontSize: '13px', fontWeight: 600, borderRadius: '6px',
                             border: '1px solid #4f46e5', background: '#fff', color: '#4f46e5',
                             cursor: marketplaceSettings.some(s => values[s.SETTINGNAME]) ? 'pointer' : 'not-allowed',
                             opacity: marketplaceSettings.some(s => values[s.SETTINGNAME]) ? 1 : 0.5,
                             flexShrink: 0 }}
                    title="Download these credentials as a CSV file"
                  >
                    Download CSV
                  </button>
                  <span style={{ fontSize: '12px', color: '#3730a3' }}>
                    Issues a new Client ID and Client Secret and fills them in below, plus an App Key,
                    Enterprise Id and External System Code if they are still empty.
                    Nothing is saved until you press Save.
                  </span>
                </div>
              )}

              {/* OVERRIDES WHERE IT IS USED (optional — the pipeline board passes a binding's). The credential is shared;
                  the values a property needs that it cannot carry (HOTELID on an OHIP chain credential) are set where it
                  is used, so they are shown beside the stored value, not saved into it. */}
              {usedOverrides.length > 0 && (
                <div style={{ padding: '10px 14px', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '6px', fontSize: '13px', color: '#3730a3', marginBottom: '14px' }}>
                  <strong>Used {overridesContext || 'here'} with</strong>{' '}
                  {usedOverrides.map(([k, v], i) => <span key={k}>{i ? ', ' : ''}{k} = <strong>{v}</strong></span>)}
                  <div style={{ fontSize: '12px', color: '#4f46e5', marginTop: '2px' }}>
                    Overrides set where the credential is used — they are not part of the credential and are not changed by saving it.
                  </div>
                </div>
              )}

              {!loading && marketplaceSettings.length > 0 && marketplaceSettings.map(s => (
                <SettingField key={s.SETTINGNAME} setting={s} value={values[s.SETTINGNAME] || ''} showCopy={isUapiMarketplace} override={overrideOf(s.SETTINGNAME)} onChange={v => setValues(prev => ({ ...prev, [s.SETTINGNAME]: v }))} />
              ))}

              {!loading && activeMarketplaceId && marketplaceSettings.length === 0 && (
                <p style={{ fontSize: '13px', color: '#94a3b8' }}>No authentication settings defined for this marketplace.</p>
              )}

              {testResult && (
                <div style={{ padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginTop: '8px',
                  background: testResult.success ? '#f0fdf4' : '#fee2e2',
                  color: testResult.success ? '#166534' : '#dc2626',
                  border: `1px solid ${testResult.success ? '#86efac' : '#fca5a5'}` }}>
                  {testResult.success ? '✓ ' : '✗ '}{testResult.message}
                </div>
              )}
            </>
          )}

          {modalTab === 'sharing' && renderSharingSection()}
          {modalTab === 'usage' && editing && <CredentialUsage credentialId={editing.credential_id} />}
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px', flexShrink: 0 }}>
          <button type="button" onClick={onClose} style={{ padding: '8px 18px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', fontSize: '13px', cursor: 'pointer' }}>
            Cancel
          </button>
          {(editing?.marketplace_id || selectedMarketplaceId) && marketplaceSettings.length > 0 && (
            <button type="button" onClick={handleTest} disabled={testing || saving}
              style={{ padding: '8px 18px', border: '1px solid #6366f1', borderRadius: '6px', background: '#fff', color: '#6366f1', fontSize: '13px', cursor: 'pointer', opacity: (testing || saving) ? 0.6 : 1 }}>
              {testing ? 'Testing...' : 'Test'}
            </button>
          )}
          <button type="button" onClick={handleSave} disabled={saving || (!selectedMarketplaceId && !editing)} style={{ padding: '8px 18px', background: '#1e40af', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', opacity: (saving || (!selectedMarketplaceId && !editing)) ? 0.6 : 1 }}>
            {saving ? 'Saving...' : 'Save Credentials'}
          </button>
        </div>
      </div>
    </div>
  );
}

// showCopy: put a Copy control on each field. Universal API only, because these are the only credential
// values the platform ISSUES — the operator has to hand them to whoever will call the API, and a
// 36-character UUID selected by dragging through an input is where a truncated or whitespace-padded
// value comes from. Every other marketplace's credentials were GIVEN to us; copying them out has no
// audience, and a copy button on a secret nobody needs to move only widens where it can end up.
function SettingField({ setting, value, onChange, showCopy = false, override = null }) {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = setting.DATATYPE === 'PASSWORD';
  const isSelect = setting.DATATYPE === 'SELECT';
  const options = isSelect
    ? (setting.AVAILABLEVALUES || '').split(/[,|]/).map(v => {
        const [label, value] = v.trim().split(':');
        return { label: label.trim(), value: (value ?? label).trim() };
      }).filter(o => o.label)
    : [];

  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500, fontSize: '13px', color: '#374151', marginBottom: '5px' }}>
        <span>
          {setting.SETTINGNAME}
          {setting.required?.toLowerCase() === 'y' && <span style={{ color: '#dc2626', marginLeft: '3px' }}>*</span>}
        </span>
        {showCopy && (
          // Disabled rather than hidden on an empty field: a control that appears and disappears as you
          // type makes the row jump, and "greyed out" already says there is nothing to copy.
          <CopyButton
            value={value}
            label="Copy"
            disabled={!value}
            title={value ? `Copy ${setting.SETTINGNAME}` : 'Nothing to copy yet'}
            className="copy-btn-inline"
          />
        )}
      </label>
      {isSelect ? (
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
        >
          <option value="">-- Select --</option>
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      ) : (
        <div style={{ position: 'relative' }}>
          <input
            type={isPassword && !showPassword ? 'password' : 'text'}
            value={value}
            onChange={e => onChange(e.target.value)}
            autoComplete={isPassword ? 'new-password' : 'off'}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', paddingRight: isPassword ? '50px' : '10px' }}
          />
          {isPassword && (
            <button
              type="button"
              onClick={() => setShowPassword(p => !p)}
              style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '12px' }}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          )}
        </div>
      )}
      {/* WHERE IT IS USED WITH ANOTHER VALUE — the screen that opened this credential overrides this setting for
          its property (a binding's hotel id, say). Shown, not saved: the override belongs to that screen. */}
      {override && (
        <div style={{ marginTop: '4px', fontSize: '12px', color: '#4338ca' }}>
          Used here as <strong>{override}</strong> (override)
        </div>
      )}
    </div>
  );
}

// THE SAME EDITOR, OPENED FROM A CREDENTIAL ID — for a screen that holds a credential_id but not its owner or settings
// (the pipeline board's Credential buttons). A thin adapter: it reads the credential in the shape CredentialModal takes
// (GET /partnermktsettings/credential, audited as a READ) and hands it over with the entity that owns it, so viewing,
// editing, sharing and testing are this tab's own and nothing is re-implemented.
export function CredentialModalById({ credentialId, onClose, onSaved, overrides = null, overridesContext = null }) {
  const [cred, setCred] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setCred(null); setError(null);
    entityMktSettingsService.getCredential(credentialId)
      .then(c => { if (!cancelled) setCred(c); })
      .catch(e => { if (!cancelled) setError(e.response?.data?.error?.message || e.response?.data?.message || e.message); });
    return () => { cancelled = true; };
  }, [credentialId]);

  if (!cred) {
    return (
      <ConfirmModal title="Credential" hideConfirm cancelLabel="Close" onCancel={onClose}
        message={error || 'Loading…'} />
    );
  }
  return (
    <CredentialModal
      entityType={cred.entity_type}
      entityId={cred.entity_id}
      editing={cred}
      overrides={overrides}
      overridesContext={overridesContext}
      onClose={onClose}
      onSaved={() => { if (onSaved) onSaved(cred); onClose(); }}
    />
  );
}

export default CredentialsTab;
